import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, AppAction, AppContextType, ViewName } from '../types';

// Initial state
const initialState: AppState = {
  currentView: 'homepage',
  selectedArtistSlug: null,
  isLoading: false,
  error: null,
};

// Reducer function
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload.view,
        selectedArtistSlug: action.payload.artistSlug || null,
        error: null, // Clear error when navigating
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    
    case 'RESET_ERROR':
      return {
        ...state,
        error: null,
      };
    
    default:
      return state;
  }
};

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Computed values - these would typically come from React Query hooks
  const artists: any[] = []; // Placeholder - will be managed by React Query
  const exhibitions: any[] = []; // Placeholder - will be managed by React Query
  const isLoading = state.isLoading;
  const error = state.error;

  const contextValue: AppContextType = {
    state,
    dispatch,
    artists,
    exhibitions,
    isLoading,
    error,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Helper functions for common actions
export const useAppActions = () => {
  const { dispatch } = useAppContext();

  const setView = (view: ViewName, artistSlug?: string) => {
    dispatch({ type: 'SET_VIEW', payload: { view, artistSlug } });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const resetError = () => {
    dispatch({ type: 'RESET_ERROR' });
  };

  return {
    setView,
    setLoading,
    setError,
    resetError,
  };
};

export default AppContext;